// src/lib/surveyView.ts
//
// What the «Satisfacción» page shows for a survey summary and its responses,
// kept out of the components so the rules are testable on their own: which
// state the page is in (never fed / no answers in the period / data), and how
// a mean, a score, a channel, a language and an answer time are written.
// A null mean is «Sin respuestas», never 0 nor a dash.
import type { TranslateFunction } from 'react-admin';
import { languageName } from '@/lib/v2MetricView';
import type { SurveyChannel, SurveyMean, SurveySummaryResponse } from '@/types/analyticsV2';

/** Display order of the channel filter and the breakdown. */
export const SURVEY_CHANNELS: SurveyChannel[] = ['assistant_phone', 'assistant_kiosk', 'kiosk_screen'];

export type SurveyPageState = 'noFeed' | 'noResponses' | 'data';

export const surveyPageState = (summary: SurveySummaryResponse): SurveyPageState => {
  if (!summary.hasFeed) return 'noFeed';
  if (summary.responses === 0) return 'noResponses';
  return 'data';
};

/** A 1–5 mean with one decimal in the reader's locale, or null when there is none. */
export const formatSurveyMean = (mean: number | null | undefined, locale: string): string | null =>
  mean === null || mean === undefined || Number.isNaN(mean)
    ? null
    : new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(mean);

export interface SurveyMeanTile {
  value: string;
  sublabel?: string;
  /** No answers: `value` is the «Sin respuestas» note, not a figure. */
  empty: boolean;
}

/** Answers a mean needs before it is stated (the API withholds it below this). */
export const SURVEY_MINIMUM_SAMPLE = 20;

/** The tile of a mean: the figure with «n respuestas» under it; «Muestra insuficiente» with the
 *  count below 20 answers; «Sin respuestas» with none. */
export const surveyMeanTile = (stat: SurveyMean, translate: TranslateFunction, locale: string): SurveyMeanTile => {
  if (stat.n > 0 && (stat.insufficientSample || stat.n < SURVEY_MINIMUM_SAMPLE || stat.mean === null)) {
    return { value: translate('resources.satisfaction.tiles.insufficient', { smart_count: stat.n }), empty: true };
  }
  const value = stat.n > 0 ? formatSurveyMean(stat.mean, locale) : null;
  if (value === null) return { value: translate('resources.satisfaction.noAnswers'), empty: true };
  return { value, sublabel: translate('resources.satisfaction.answerCount', { smart_count: stat.n }), empty: false };
};

/** One journey's score for a question, or null when the guest did not answer it. */
export const formatSurveyScore = (score: number | null | undefined): string | null =>
  score === null || score === undefined ? null : String(score);

export const surveyChannelLabel = (channel: string, translate: TranslateFunction): string =>
  translate(`resources.satisfaction.channels.${channel}`, { _: channel });

export const surveyLanguageLabel = (code: string | null | undefined, locale: string): string | null =>
  code ? languageName(code, locale) ?? code.toUpperCase() : null;

/** The answer time in the property's own time zone («03/10/2026, 14:05»). */
export const formatSurveyAnsweredAt = (iso: string, timeZone: string | undefined, locale: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: timeZone || undefined }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(date);
  }
};

/** Breakdown rows in the fixed channel order, unknown channels last. */
export const sortByChannel = <T extends { channel: string }>(rows: T[]): T[] => {
  const rank = (c: string) => {
    const i = SURVEY_CHANNELS.indexOf(c as SurveyChannel);
    return i === -1 ? SURVEY_CHANNELS.length : i;
  };
  return [...rows].sort((a, b) => rank(a.channel) - rank(b.channel));
};
